import { useCallback } from 'react';

import { Box, Text } from '@talos/kyoko';

import { PredictionCardForm, PredictionCardFormActions } from './PredictionCardForm';
import { SelectorBody } from './styles';
import type { PredictionCardFormValues } from './usePredictionCardForm';

interface PredictionCardSelectorProps {
  initialValues: PredictionCardFormValues;
  onValuesChange: (values: PredictionCardFormValues) => void;
  onCancel: () => void;
  onCreateCard: (values: PredictionCardFormValues) => void;
}

export function PredictionCardSelector({
  initialValues,
  onValuesChange,
  onCancel,
  onCreateCard,
}: PredictionCardSelectorProps) {
  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>, form: PredictionCardFormValues, primaryDisabled: boolean) => {
      e.preventDefault();
      if (primaryDisabled) {
        return;
      }
      onCreateCard(form);
    },
    [onCreateCard]
  );

  return (
    <PredictionCardForm mode="create" initialValues={initialValues} onValuesChange={onValuesChange}>
      {({ form, fields, primaryDisabled }) => {
        // Market is resolved automatically, so the hint only goes away once the user picks a field themselves.
        const hasSelection = !!form.eventSeries || !!form.event || !!form.outcome;

        return (
          <SelectorBody
            as="form"
            onSubmit={(e: React.FormEvent<HTMLFormElement>) => handleSubmit(e, form, primaryDisabled)}
          >
            {!hasSelection && (
              <Text
                color="colorTextMuted"
                size="fontSizeSmall"
                mb="spacing12"
                data-testid="prediction-card-selector-hint"
              >
                Select an event, optionally pick a single outcome, then create the card.
              </Text>
            )}
            <Box flex="1" minHeight={0} overflowX="hidden" overflowY="auto" w="100%">
              {fields}
            </Box>
            <PredictionCardFormActions mode="create" primaryDisabled={primaryDisabled} onCancel={onCancel} />
          </SelectorBody>
        );
      }}
    </PredictionCardForm>
  );
}
